import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StandardLayout from '../../../components/layout/StandardLayout';
import { getAllExamSubcategoriesWithCounts, deleteExamSubcategory } from '../../../services/examAPIService';
import { PencilIcon, TrashIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAlert } from '../../../context/AlertContext';

const ExamSubcategoryList = () => {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const { showAlert } = useAlert();

    useEffect(() => {
        loadExams();
    }, []);

    const loadExams = async () => {
        try {
            setLoading(true);
            const data = await getAllExamSubcategoriesWithCounts();
            setExams(data);
            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Prüfungsteile');
            console.error('Error loading exams:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (subcategory, examTitle) => {
        setDeleteConfirmation({ ...subcategory, examTitle });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation) return;

        try {
            await deleteExamSubcategory(deleteConfirmation.id);
            // Aktualisiere die lokale Liste
            setExams(prevExams => prevExams.map(exam => ({
                ...exam,
                subcategories: exam.subcategories.filter(sub => sub.id !== deleteConfirmation.id)
            })));
            showAlert('Prüfungsteil wurde erfolgreich gelöscht', 'success', true);
            setDeleteConfirmation(null);
        } catch (err) {
            setError('Fehler beim Löschen des Prüfungsteils');
            console.error('Error deleting exam subcategory:', err);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation(null);
    };

    if (loading) {
        return (
            <StandardLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    // Flache Liste aller Prüfungsteile mit Prüfungstitel
    const allSubcategories = exams.flatMap(exam => 
        exam.subcategories.map(sub => ({
            ...sub,
            examTitle: exam.title
        }))
    );

    return (
        <StandardLayout>
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Prüfungsteile</h1>
                    <Link
                        to="/admin/exam-parts/add"
                        className="btn btn-primary"
                    >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Neuer Prüfungsteil
                    </Link>
                </div>

                {error && (
                    <div className="alert alert-error mb-4">
                        <XMarkIcon className="w-6 h-6" />
                        <span>{error}</span>
                    </div>
                )}

                {deleteConfirmation && (
                    <div className="alert alert-warning mb-4">
                        <span>
                            Möchten Sie den Prüfungsteil "{deleteConfirmation.title}" 
                            von der Prüfung "{deleteConfirmation.examTitle}" wirklich löschen?
                        </span>
                        <div>
                            <button
                                className="btn btn-sm btn-error mr-2"
                                onClick={handleDeleteConfirm}
                            >
                                Löschen
                            </button>
                            <button
                                className="btn btn-sm"
                                onClick={handleDeleteCancel}
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="table w-full">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Titel</th>
                                <th>Prüfung</th>
                                <th>Anzahl Fragen</th>
                                <th className="text-right">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allSubcategories.map(subcategory => (
                                <tr key={subcategory.id} className="hover">
                                    <td>{subcategory.id}</td>
                                    <td>{subcategory.title}</td>
                                    <td>{subcategory.examTitle}</td>
                                    <td>{subcategory.question_count || 0}</td>
                                    <td className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Link
                                                to={`/admin/exam-parts/${subcategory.id}/edit`}
                                                className="btn btn-sm btn-ghost"
                                                title="Bearbeiten"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </Link>
                                            <button
                                                onClick={() => handleDeleteClick(subcategory, subcategory.examTitle)}
                                                className="btn btn-sm btn-ghost text-error"
                                                title="Löschen"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </StandardLayout>
    );
};

export default ExamSubcategoryList; 